import { Component, h, Prop, Listen, Element } from '@stencil/core';
import { CookieConsentOptions, CookieConsentType, GtagSettings } from './cookie-consent.interface';
import { applyOptions } from '../../../utils';
import { cookies, storage } from '../../../services';
import { isValidCookieValue } from './cookie-consent.utils';
import { Build, JSXAttributes } from '@stencil/core/internal';

// eslint-disable-next-line @typescript-eslint/ban-types
declare let gtag: Function;

/**
 * The cookie consent
 *
 * ## Anatomy
 *
 * - Information *
 * - Settings
 *  - Settings information *
 *  - Default gtag values
 *  - Settings choices
 *    - Choices
 *      - Basic
 *      - Complete
 *    - Choice details
 *      - What we do
 *      - What we don't
 *      - Gtag values per choice
 * - Actions
 *  - Accept
 *  - Deny
 *
 * (*) Information is plain html. Can include links.
 *
 */
@Component({
    tag: 'web-cookie-consent',
    shadow: false
})
export class WebCookieConsent implements CookieConsentOptions {
    private COOKIE_CONSENT_TYPE: { All: CookieConsentType; Basic: CookieConsentType } = { All: 'all', Basic: 'basic' };

    @Element() rootElement: HTMLElement;

    @Prop() gTagSettings: GtagSettings;
    @Prop() options: CookieConsentOptions | string;
    @Prop() cookieName: string;

    @Listen('click', { capture: true })
    handleClick(event: MouseEvent): void {
        const target = (event.target as HTMLElement).closest<HTMLElement>('[data-element]');
        if (target) {
            event.preventDefault();
            event.stopPropagation();
            this.handleClickOnTarget(target);
        }
    }

    componentWillLoad(): void {
        applyOptions(this);
    }

    componentDidLoad(): void {
        if (!Build.isBrowser) {
            return;
        }

        const templates = this.rootElement.querySelectorAll('template');

        Array.from(templates).forEach((template) => {
            const clone = document.importNode(template.content, true);
            template.parentNode.appendChild(clone);
        });

        // check if a valid cookie setting is present and show overlay accordingly
        // TODO: get localized cookie-settings param name from outside of script per country
        const params = new URLSearchParams(window.location.search);
        const hasCookieSettingsParam = params.has('cookie-settings') || params.has('cookie-instellingen');

        const cookieValue = cookies.getCookie(this.cookieName)?.value as CookieConsentType | undefined;
        const showOverlay = cookieValue === undefined || isValidCookieValue(cookieValue) === false;

        this.toggleOverlay(showOverlay, hasCookieSettingsParam);
    }

    /**
     * Handles click events on the cookie consent buttons.
     * @param target The element that was clicked.
     */
    private handleClickOnTarget(target: HTMLElement): void {
        const elementName = target.dataset?.element;
        if (elementName === `${this.COOKIE_CONSENT_TYPE.All}-button`) {
            this.savePreference(this.COOKIE_CONSENT_TYPE.All);
        }
        if (elementName === `${this.COOKIE_CONSENT_TYPE.Basic}-button`) {
            this.savePreference(this.COOKIE_CONSENT_TYPE.Basic);
        }
        if (elementName === `${this.COOKIE_CONSENT_TYPE.All}-info-link`) {
            this.toggleMoreInformation(this.COOKIE_CONSENT_TYPE.All, this.COOKIE_CONSENT_TYPE.Basic);
        }
        if (elementName === `${this.COOKIE_CONSENT_TYPE.Basic}-info-link`) {
            this.toggleMoreInformation(this.COOKIE_CONSENT_TYPE.Basic, this.COOKIE_CONSENT_TYPE.All);
        }
    }

    /**
     * Toggles the visibility of the information containers.
     * @param consentType The consent type to be displayed.
     * @param otherConsentType The other consent type to be hidden.
     */
    private toggleMoreInformation(consentType: CookieConsentType, otherConsentType: CookieConsentType): void {
        const selectedInfoContainer = this.rootElement.querySelector(
            `[data-element=${consentType}-info]`
        ) as HTMLElement;
        const unselectedInfoContainer = this.rootElement.querySelector(
            `[data-element=${otherConsentType}-info]`
        ) as HTMLElement;

        this.toggleVisibilityContainer(selectedInfoContainer);

        unselectedInfoContainer.style.setProperty('display', 'none');

        // rotate the more info icon
        this.toggleMoreInfoIcon(consentType);
        this.toggleMoreInfoIcon(otherConsentType);

        selectedInfoContainer.scrollIntoView({ behavior: 'smooth' });
    }

    /**
     * Toggles the visibility of the overlay.
     * @param toggle Whether to open or close the overlay.
     * @param hasCookieSettingsParam Whether the cookie settings parameter is present.
     */
    private toggleOverlay(toggle: boolean, hasCookieSettingsParam: boolean) {
        const overlay = this.rootElement.closest('web-overlay');
        toggle ? overlay.open({ modal: toggle || !hasCookieSettingsParam }) : overlay.close();
    }

    /**
     * Toggles the visibility of the more information icon.
     * @param consentType The consent type to be displayed. This can be 'all' or 'basic'.
     */
    private toggleMoreInfoIcon(consentType: CookieConsentType): void {
        const display =
            (this.rootElement.querySelector(`[data-element=${consentType}-info]`) as HTMLElement).style.display ||
            'none';

        display === 'none'
            ? (
                  this.rootElement.querySelector(`[data-element=${consentType}-info-link] svg`) as HTMLElement
              ).style.setProperty('transform', 'rotate(180deg)')
            : (
                  this.rootElement.querySelector(`[data-element=${consentType}-info-link] svg`) as HTMLElement
              ).style.setProperty('transform', 'rotate(0)');
    }

    /**
     * Toggles the visibility of a container.
     * @param container The container to toggle.
     */
    private toggleVisibilityContainer(container: HTMLElement): void {
        const display = container.style.display || 'none';
        container.style.setProperty('display', display === 'none' ? 'block' : 'none');
    }

    /**
     * Saves the user's cookie preference.
     * @param consentType The consent type to be saved.
     */
    private savePreference = (consentType: CookieConsentType): void => {
        this.updateGtagConsent(consentType);
        this.setCookie(consentType);

        this.cleanUrl();
        this.toggleOverlay(false, false);
    };

    /**
     * Cleans the URL by removing the cookie settings parameters.
     */
    private cleanUrl() {
        // deeplink to ?cookie-settings opens dialog, should be removed before refresh/replace
        // other deeplinks might send params (eg ?utm_source) that should NOT be removed
        const url = new URL(window.location.href);
        url.searchParams.delete('cookie-settings');
        url.searchParams.delete('cookie-instellingen');

        const result = url.toString();
        window.location.replace(result);
    }

    /**
     * Push the gtag consent settings to the dataLayer and save to local storage
     * @param consentType will define the consent settings to be pushed to the dataLayer
     */
    private updateGtagConsent(consentType: CookieConsentType): void {
        const consentSettings = this.gTagSettings[consentType]['gtag'];
        if (typeof gtag === 'function') {
            gtag('consent', 'update', consentSettings);
        }
        storage.setItem('consentMode', consentSettings);
    }

    /**
     * Set the cookie with the proper value
     * @param consentType the consent type to be saved in the cookie
     */
    private setCookie(consentType: CookieConsentType): void {
        cookies.setCookie(this.cookieName, consentType, { sameSite: 'lax' });
    }

    public render = (): JSXAttributes<Element> => <slot />;
}
